// src/pages/SettingsPage.jsx
import React, { useState, useEffect } from 'react';
import '../assets/css/SettingsPage.css';

export default function SettingsPage() {
  const [tab, setTab] = useState('general');

  //
  // ─── GENERAL SETTINGS ───────────────────────────────────────────────────────
  //
  const [general, setGeneral] = useState({ siteName: '', pageSize: 10 });
  const [generalMsg, setGeneralMsg] = useState('');

  useEffect(() => {
    if (tab !== 'general') return;
    // Load existing general settings
    fetch('/api/admin/get_general_settings.php', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ /* auth credentials if needed */ })
    })
      .then(res => res.json())
      .then(json => {
        if (json.status === 200) {
          setGeneral({
            siteName: json.data.site_name,
            pageSize:  json.data.default_page_size
          });
        }
      });
  }, [tab]);

  const handleGeneralSubmit = e => {
    e.preventDefault();
    setGeneralMsg('Saving...');
    fetch('/api/admin/update_general_settings.php', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(general)
    })
      .then(res => res.json())
      .then(json => {
        setGeneralMsg(json.message || 'Saved.');
      })
      .catch(() => setGeneralMsg('Error saving settings.'));
  };


  //
  // ─── PROFILE SETTINGS ───────────────────────────────────────────────────────
  //
  const [profile, setProfile] = useState({ fullName: '', email: '', password: '' });
  const [profileMsg, setProfileMsg] = useState('');

  useEffect(() => {
    if (tab !== 'profile') return;
    // Load admin’s profile
    fetch('/api/admin/get_profile.php', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ /* auth credentials */ })
    })
      .then(res => res.json())
      .then(json => {
        if (json.status === 200) {
          setProfile({
            fullName: json.data.full_name,
            email:    json.data.email,
            password: ''           // leave blank
          });
        }
      });
  }, [tab]);

  const handleProfileSubmit = e => {
    e.preventDefault();
    setProfileMsg('Updating...');
    fetch('/api/admin/update_profile.php', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(profile)
    })
      .then(res => res.json())
      .then(json => setProfileMsg(json.message || 'Profile updated.'))
      .catch(() => setProfileMsg('Error updating profile.'));
  };


  //
  // ─── USER MANAGEMENT ────────────────────────────────────────────────────────
  //
  const [users, setUsers]   = useState([]);
  const [search, setSearch] = useState('');
  const [page, setPage]     = useState(1);
  const [userMsg, setUserMsg] = useState('');

  useEffect(() => {
    if (tab !== 'users') return;
    fetch('/api/admin/fetch_users.php', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ search, page })
    })
      .then(res => res.json())
      .then(json => setUsers(json.data || []))
      .catch(() => setUsers([]));
  }, [tab, search, page]);

  const handleUserAction = (userId, action) => {
    setUserMsg(`Performing ${action}…`);
    fetch('/api/admin/user_action.php', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ user_id: userId, action })
    })
      .then(res => res.json())
      .then(json => {
        setUserMsg(json.message || 'Done.');
        // refresh users list
        setTimeout(() => setUserMsg(''), 2000);
        setUsers(u => u.map(u => 
          u.user_id === userId
            ? { ...u, account_status: json.data?.new_status || u.account_status }
            : u
        ));
      })
      .catch(() => setUserMsg('Action failed.'));
  };


  //
  // ─── SPONSORSHIP ────────────────────────────────────────────────────────────
  //
  const [sponsorData, setSponsorData] = useState({ title: '', link: '', imageUrl: '' });
  const [sponsorMsg, setSponsorMsg]   = useState('');

  const handleCreateSponsor = e => {
    e.preventDefault();
    setSponsorMsg('Creating…');
    fetch('/api/admin/create_sponsorship.php', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(sponsorData)
    })
      .then(res => res.json())
      .then(json => setSponsorMsg(json.message || 'Sponsor post created.'))
      .catch(() => setSponsorMsg('Error creating sponsor.'));
  };


  //
  // ─── RENDER ────────────────────────────────────────────────────────────────
  //
  return (
    <div className="settings container">
      <h1 className="settings__title">Admin Settings</h1>

      <div className="settings__tabs">
        {['general','profile','users','sponsorship'].map(t => (
          <button
            key={t}
            className={tab === t ? 'active' : ''}
            onClick={() => setTab(t)}
          >
            {t === 'sponsorship' ? 'Sponsor Posts' :
             t.charAt(0).toUpperCase() + t.slice(1)}
          </button>
        ))}
      </div>

      <div className="settings__panel">

        {tab === 'general' && (
          <>
            {generalMsg && <p className="status-msg">{generalMsg}</p>}
            <form className="settings__form" onSubmit={handleGeneralSubmit}>
              <label>
                Site Name
                <input
                  type="text"
                  value={general.siteName}
                  onChange={e => setGeneral(g => ({ ...g, siteName: e.target.value }))}
                />
              </label>
              <label>
                Default Page Size
                <input
                  type="number"
                  value={general.pageSize}
                  min="1"
                  onChange={e => setGeneral(g => ({ ...g, pageSize: +e.target.value }))}
                />
              </label>
              <button type="submit">Save General</button>
            </form>
          </>
        )}

        {tab === 'profile' && (
          <>
            {profileMsg && <p className="status-msg">{profileMsg}</p>}
            <form className="settings__form" onSubmit={handleProfileSubmit}>
              <label>
                Your Name
                <input
                  type="text"
                  value={profile.fullName}
                  onChange={e => setProfile(p => ({ ...p, fullName: e.target.value }))}
                />
              </label>
              <label>
                Email Address
                <input
                  type="email"
                  value={profile.email}
                  onChange={e => setProfile(p => ({ ...p, email: e.target.value }))}
                />
              </label>
              <label>
                New Password
                <input
                  type="password"
                  value={profile.password}
                  onChange={e => setProfile(p => ({ ...p, password: e.target.value }))}
                />
              </label>
              <button type="submit">Save Profile</button>
            </form>
          </>
        )}

        {tab === 'users' && (
          <div className="dashboard-reports">
            {userMsg && <p className="status-msg">{userMsg}</p>}
            <div className="reports-header">
              <h2>User Management</h2>
              <p>Search, block, reset credentials or add new users.</p>
            </div>

            <div className="user-search">
              <input
                type="text"
                placeholder="Search by name or email…"
                value={search}
                onChange={e => { setSearch(e.target.value); setPage(1); }}
              />
            </div>

            <div className="reports-grid">
              {users.length ? users.map(u => (
                <div className="report-card" key={u.user_id}>
                  <div className="report-header">
                    <p><strong>{u.full_name}</strong> (@{u.username})</p>
                    <p>Status: {u.account_status}</p>
                  </div>
                  <div className="report-media-grid">
                    <button onClick={() => handleUserAction(u.user_id, 'resetPassword')}>
                      Reset Password
                    </button>
                    <button onClick={() => handleUserAction(u.user_id, 'updateEmail')}>
                      Reset Email
                    </button>
                    <button onClick={() => handleUserAction(u.user_id, 'block')}>
                      {u.account_status === 'active' ? 'Block' : 'Unblock'}
                    </button>
                  </div>
                </div>
              )) : (
                <p className="status-msg">No users found.</p>
              )}
            </div>

            <div className="pagination">
              <button
                className="pagination-btn"
                disabled={page <= 1}
                onClick={()=>setPage(p => p - 1)}
              >Prev</button>
              <span className="pagination-info">Page {page}</span>
              <button
                className="pagination-btn"
                onClick={()=>setPage(p => p + 1)}
              >Next</button>
            </div>
          </div>
        )}

        {tab === 'sponsorship' && (
          <>
            {sponsorMsg && <p className="status-msg">{sponsorMsg}</p>}
            <form className="settings__form" onSubmit={handleCreateSponsor}>
              <label>
                Title
                <input
                  type="text"
                  value={sponsorData.title}
                  onChange={e => setSponsorData(d => ({ ...d, title: e.target.value }))}
                />
              </label>
              <label>
                Redirect Link
                <input
                  type="url"
                  value={sponsorData.link}
                  onChange={e => setSponsorData(d => ({ ...d, link: e.target.value }))}
                />
              </label>
              <label>
                Image URL
                <input
                  type="text"
                  value={sponsorData.imageUrl}
                  onChange={e => setSponsorData(d => ({ ...d, imageUrl: e.target.value }))}
                />
              </label>
              <button type="submit">Create Sponsor Post</button>
            </form>
          </>
        )}

      </div>
    </div>
  );
}
